import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, HardHat, MessageSquare, CalendarClock,
  Settings as SettingsIcon, LogOut, Activity, Sparkles, ChevronRight, UserPlus, BookOpen, FileText, Mail, Zap, Share2, Target, Bell, Menu, X, Euro, Briefcase,
} from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const NAV = [
  { to: "/app", label: "Cockpit", icon: LayoutDashboard, testid: "nav-cockpit" },
  { to: "/app/mission", label: "Mission Control", icon: Zap, testid: "nav-mission", highlight: true },
  { to: "/app/pilotes", label: "Pilotes", icon: Users, testid: "nav-pilotes" },
  { to: "/app/prospects", label: "Prospects", icon: UserPlus, testid: "nav-prospects" },
  { to: "/app/prospection", label: "Prospection IA", icon: Target, testid: "nav-prospection", highlight: true },
  { to: "/app/relances", label: "Relances", icon: Bell, testid: "nav-relances" },
  { to: "/app/business", label: "Business Center", icon: Briefcase, testid: "nav-business", highlight: true },
  { to: "/app/abonnements", label: "Abonnements", icon: Euro, testid: "nav-abonnements" },
  { to: "/app/agents", label: "Mon équipe", icon: Sparkles, testid: "nav-team" },
  { to: "/app/documents", label: "Documents", icon: FileText, testid: "nav-documents" },
  { to: "/app/emails", label: "Emails", icon: Mail, testid: "nav-emails" },
  { to: "/app/chat", label: "Chat", icon: MessageSquare, testid: "nav-agents" },
  { to: "/app/briefings", label: "Briefings", icon: CalendarClock, testid: "nav-briefings" },
  { to: "/app/settings", label: "Paramètres", icon: SettingsIcon, testid: "nav-settings" },
];

export default function ConsoleShell({ children, title, breadcrumb, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dueCount, setDueCount] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close the mobile drawer whenever the route changes
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/prospects/due-followups");
        if (!cancelled) setDueCount(data?.count || 0);
      } catch {
        /* silent */
      }
    };
    fetchCount();
    const int = setInterval(fetchCount, 60000); // refresh every 60s
    // Also refetch when the user navigates (in case they just marked one sent)
    return () => { cancelled = true; clearInterval(int); };
  }, [location.pathname]);

  const badgeFor = (testid) => {
    if (testid === "nav-relances" && dueCount > 0) return dueCount;
    return null;
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-[220px] flex-shrink-0 border-r border-white/10 flex-col fixed inset-y-0" data-testid="shell-sidebar">
        <div className="px-5 py-5 border-b border-white/10">
          <Link to="/app" className="flex items-center gap-2.5" data-testid="shell-logo">
            <img src="/brand/gabryx-logo.png" alt="GABRYX" className="w-9 h-9 object-contain" />
            <div>
              <div className="font-display font-semibold tracking-tight text-[15px] leading-none">GABRYX</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">AI Cockpit</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active =
              n.to === "/app"
                ? location.pathname === "/app"
                : location.pathname.startsWith(n.to);
            const badge = badgeFor(n.testid);
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`flex items-center gap-2.5 px-3 py-2 text-[13px] transition ${
                  active
                    ? "bg-white/10 text-white"
                    : n.highlight
                      ? "text-amber-300 hover:bg-amber-500/10"
                      : "text-zinc-400 hover:bg-white/5 hover:text-white"
                }`}
              >
                <Icon className={`w-4 h-4 ${n.highlight && !active ? "text-amber-400" : ""}`} />
                <span>{n.label}</span>
                {badge != null && (
                  <span
                    data-testid={`${n.testid}-badge`}
                    className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-mono font-medium bg-red-500/90 text-white"
                  >
                    {badge}
                  </span>
                )}
                {active && badge == null && <span className="ml-auto w-1 h-4 bg-white" />}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-white/10 p-3">
          <div className="flex items-center gap-1 text-[10px] font-mono text-zinc-500 mb-3">
            <Activity className="w-3 h-3 text-emerald-400 pulse-dot" />
            <span className="ml-1">CHANTIX live</span>
          </div>
          <div className="flex items-center gap-2 px-2">
            <div className="w-7 h-7 bg-white/10 rounded-full grid place-items-center text-xs font-bold flex-shrink-0">
              {(user?.email || "?")[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] truncate">{user?.email}</div>
              <div className="text-[10px] text-zinc-500 font-mono">admin</div>
            </div>
            <button
              onClick={() => { logout(); navigate("/"); }}
              data-testid="shell-logout-btn"
              className="p-1.5 hover:bg-white/5 transition"
              title="Se déconnecter"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar (sidebar collapsed) */}
      <header className="md:hidden fixed top-0 inset-x-0 z-40 glass border-b border-white/10 h-14 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMobileNavOpen(true)}
            className="p-2 -ml-2 hover:bg-white/5 relative"
            data-testid="mobile-menu-btn"
            aria-label="Ouvrir le menu"
          >
            <Menu className="w-5 h-5" />
            {dueCount > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500" />
            )}
          </button>
          <Link to="/app" className="flex items-center gap-2">
            <img src="/brand/gabryx-logo.png" alt="GABRYX" className="w-7 h-7 object-contain" />
            <span className="font-display font-semibold">GABRYX</span>
          </Link>
        </div>
        <button
          onClick={() => { logout(); navigate("/"); }}
          className="p-2 hover:bg-white/5"
          aria-label="Se déconnecter"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </header>

      {/* Mobile nav drawer */}
      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-nav-drawer">
          <div
            className="absolute inset-0 bg-black/70"
            onClick={() => setMobileNavOpen(false)}
          />
          <aside className="relative w-[80%] max-w-[320px] bg-[#0a0a0a] border-r border-white/10 flex flex-col shadow-2xl animate-slide-in">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <img src="/brand/gabryx-logo.png" alt="GABRYX" className="w-8 h-8 object-contain" />
                <div>
                  <div className="font-display font-semibold tracking-tight text-[14px] leading-none">GABRYX</div>
                  <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">AI Cockpit</div>
                </div>
              </div>
              <button
                onClick={() => setMobileNavOpen(false)}
                className="p-1.5 hover:bg-white/5"
                data-testid="mobile-nav-close"
                aria-label="Fermer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
              {NAV.map((n) => {
                const Icon = n.icon;
                const active =
                  n.to === "/app"
                    ? location.pathname === "/app"
                    : location.pathname.startsWith(n.to);
                const badge = badgeFor(n.testid);
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    onClick={() => setMobileNavOpen(false)}
                    data-testid={`mobile-${n.testid}`}
                    className={`flex items-center gap-3 px-3 py-3 text-[14px] transition ${
                      active
                        ? "bg-white/10 text-white"
                        : n.highlight
                          ? "text-amber-300 hover:bg-amber-500/10"
                          : "text-zinc-300 hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${n.highlight && !active ? "text-amber-400" : ""}`} />
                    <span className="flex-1">{n.label}</span>
                    {badge != null && (
                      <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 text-[11px] font-mono font-medium bg-red-500/90 text-white">
                        {badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
            <div className="border-t border-white/10 p-3">
              <div className="flex items-center gap-2 px-2">
                <div className="w-8 h-8 bg-white/10 rounded-full grid place-items-center text-xs font-bold flex-shrink-0">
                  {(user?.email || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] truncate">{user?.email}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">admin</div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 md:pl-[220px] pt-14 md:pt-0 pb-16 md:pb-0">
        {(title || breadcrumb || actions) && (
          <div className="sticky top-0 md:top-0 z-30 glass border-b border-white/10 h-14 flex items-center justify-between px-5 lg:px-8">
            <div className="min-w-0 flex items-center gap-3">
              {breadcrumb && (
                <div className="hidden sm:flex items-center gap-1 text-xs text-zinc-500 font-mono">
                  {breadcrumb.map((b, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <ChevronRight className="w-3 h-3 text-zinc-700" />}
                      <span className={i === breadcrumb.length - 1 ? "text-zinc-300" : ""}>{b}</span>
                    </React.Fragment>
                  ))}
                </div>
              )}
              {title && (
                <div className="font-display text-lg font-semibold tracking-tight truncate">{title}</div>
              )}
            </div>
            {actions && <div className="flex items-center gap-2">{actions}</div>}
          </div>
        )}
        {children}
      </main>

      {/* Mobile bottom bar — 3 primary actions at thumb reach */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-[10000] glass border-t border-white/10 h-14 grid grid-cols-4 pb-[env(safe-area-inset-bottom)]"
        data-testid="mobile-bottom-bar"
      >
        {[
          { to: "/app", label: "Cockpit", icon: LayoutDashboard, testid: "bottom-cockpit" },
          { to: "/app/prospection", label: "Prospection", icon: Target, testid: "bottom-prospection" },
          { to: "/app/relances", label: "Relances", icon: Bell, testid: "bottom-relances", badge: dueCount },
          { to: "/app/abonnements", label: "Abonnements", icon: Euro, testid: "bottom-abonnements" },
        ].map((a) => {
          const Icon = a.icon;
          const active =
            a.to === "/app"
              ? location.pathname === "/app"
              : location.pathname.startsWith(a.to);
          return (
            <Link
              key={a.to}
              to={a.to}
              data-testid={a.testid}
              className={`flex flex-col items-center justify-center gap-0.5 text-[10px] transition relative ${
                active ? "text-white" : "text-zinc-500 hover:text-white"
              }`}
            >
              <Icon className="w-5 h-5" />
              <span>{a.label}</span>
              {a.badge > 0 && (
                <span className="absolute top-1 right-1/4 min-w-[16px] h-[16px] px-1 text-[10px] font-mono bg-red-500 text-white flex items-center justify-center">
                  {a.badge}
                </span>
              )}
              {active && <span className="absolute top-0 inset-x-4 h-[2px] bg-white" />}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
